/**
 * Representatives Tour
 * Find and explore your elected officials
 *
 * This tour navigates from the legislators list to a specific legislator's detail page.
 * Uses sessionStorage to persist tour state across page navigation.
 */

import type { Tour } from '@/types/onboarding';
import { saveTourResumeState } from './committee-tour';

const PERSISTENT_HIGHLIGHT_CLASS = 'tour-persistent-highlight';

/**
 * Add persistent highlight to Legislators nav item as a visual anchor
 */
function addLegislatorsNavHighlight() {
  const legislatorsNav = document.querySelector('[data-tour="nav-legislators"]');
  legislatorsNav?.classList.add(PERSISTENT_HIGHLIGHT_CLASS);
}

/**
 * Remove persistent highlight when tour ends.
 * Exported so it can be called on tour completion/skip.
 */
export function cleanupRepresentativesTour() {
  const legislatorsNav = document.querySelector('[data-tour="nav-legislators"]');
  legislatorsNav?.classList.remove(PERSISTENT_HIGHLIGHT_CLASS);
}

/**
 * Navigate to a specific legislator's detail page.
 * Saves tour state so it can resume after navigation.
 */
function navigateToLegislatorDetail(
  _element: Element | undefined,
  _step: unknown,
  opts: { driver: { destroy: () => void } }
) {
  // Save state to resume at step 5 (index 4) after navigation
  saveTourResumeState('representatives', 4);

  // Stop the current driver instance before navigation
  opts.driver.destroy();

  // Navigate to legislator detail page (ID 267 - a consistent example)
  window.location.href = '/legislators/267';
}

/**
 * Switch to a specific tab and advance to next step
 */
function switchTabAndAdvance(tabValue: string, driverInstance: { moveNext: () => void }) {
  window.dispatchEvent(
    new CustomEvent('billbee:switch-legislator-tab', { detail: { tab: tabValue } })
  );
  setTimeout(() => {
    driverInstance.moveNext();
  }, 250);
}

export const representativesTour: Tour = {
  id: 'representatives',
  name: 'Your Representatives',
  description: 'Find your legislators and see how they vote',
  startRoute: '/legislators',
  steps: [
    {
      // Step 1: Welcome with nav highlight
      element: '[data-tour="nav-legislators"]',
      popover: {
        title: 'Meet Your Legislators',
        description:
          "Here you'll find all members of the Kansas Legislature. Let's see how to find your representatives.",
        side: 'bottom',
        align: 'center',
      },
      onHighlightStarted: addLegislatorsNavHighlight,
    },
    {
      // Step 2: County and District filters
      element: '[data-tour="legislators-county-district"]',
      popover: {
        title: 'Find by location',
        description:
          'Select your county or district number to see who represents your area in the Kansas Legislature.',
        side: 'bottom',
        align: 'start',
      },
    },
    {
      // Step 3: Chamber and Party filters
      element: '[data-tour="legislators-chamber-party"]',
      popover: {
        title: 'Filter by chamber or party',
        description:
          'Narrow your search by chamber (House or Senate) or political party affiliation.',
        side: 'bottom',
        align: 'start',
      },
    },
    {
      // Step 4: Legislator card - click to navigate
      element: '[data-tour="legislators-list"]',
      popover: {
        title: 'View a legislator',
        description:
          "Click any legislator card to see their full profile. Let's explore one together.",
        side: 'top',
        align: 'center',
        onNextClick: navigateToLegislatorDetail,
      },
    },
    {
      // Step 5: Legislator header (after navigation)
      element: '[data-tour="legislator-header"]',
      popover: {
        title: 'Legislator profile',
        description:
          "This is the legislator's profile page with contact info, voting record, and committee assignments.",
        side: 'bottom',
        align: 'center',
      },
    },
    {
      // Step 6: Tabs overview
      element: '[data-tour="legislator-tabs"]',
      popover: {
        title: 'Explore their record',
        description:
          'Use these tabs to see their committee memberships, sponsored bills, and voting history.',
        side: 'top',
        align: 'center',
        onNextClick: (_el, _step, { driver }) => switchTabAndAdvance('committees', driver),
      },
    },
    {
      // Step 7: Committees content (after tab switch)
      element: '[data-tour="legislator-committees-content"]',
      popover: {
        title: 'Committee assignments',
        description:
          'See which committees this legislator serves on. Committees shape legislation before it reaches the floor.',
        side: 'top',
        align: 'center',
        onNextClick: (_el, _step, { driver }) => switchTabAndAdvance('bills', driver),
      },
    },
    {
      // Step 8: Bills content (final step)
      element: '[data-tour="legislator-bills-content"]',
      popover: {
        title: 'Sponsored bills',
        description:
          'View bills they introduced or co-sponsored. Track any that matter to you to stay informed on their progress.',
        side: 'top',
        align: 'center',
      },
      onDeselected: cleanupRepresentativesTour,
    },
  ],
};
